import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Link,
  Banner,
  Collapsible,
  List,
  TextContainer,
  Divider,
  Button,
} from '@shopify/polaris';

export default function SetUpStanStore() {
  const [open, setOpen] = useState(false);

  return (
    <Page title="Set Up Google Analytics on Your Stan.Store">
      <Layout>
        <Layout.Section>
          <Banner title="Why Google Analytics Matters" status="info">
            Google Analytics ensures Baobrain can show your Stan.Store pageviews and engagement data.
          </Banner>
        </Layout.Section>

        <Layout.Section>
          <Card sectioned>
            <Text variant="headingLg">Steps to Add Google Analytics</Text>

            <List type="number">
              <List.Item>
                Visit{' '}
                <Link url="https://admin.stan.store/dashboard/" external>
                  Stan Dashboard
                </Link>{' '}
                and sign in.
              </List.Item>
              <List.Item>
                Click your user icon (top-right) → <strong>Settings</strong>.
              </List.Item>
              <List.Item>
                In Settings, click on <strong>Profile</strong>.
              </List.Item>
              <List.Item>
                Scroll to the <strong>Analytics</strong> section.
              </List.Item>
              <List.Item>
                Enter your Google Analytics Measurement ID (e.g., <code>G-XXXXXXXXXX</code>).
              </List.Item>
              <List.Item>
                <Link as="button" onClick={() => setOpen(!open)}>
                  Need a Google Analytics Account?
                </Link>
                <Collapsible open={open} id="ga-collapse">
                  <TextContainer>
                    <List type="number">
                      <List.Item>
                        <Text>
                          <strong>Go to Google Analytics:</strong> Open your browser and go to{' '}
                          <Link url="https://analytics.google.com/" external>
                            analytics.google.com
                          </Link>.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>
                          <strong>First Time Setup:</strong> If this is your first time, click{' '}
                          <em>Start measuring</em>. If you already have an account, go to{' '}
                          <em>Admin</em> (gear icon, bottom-left), then click <em>Create &gt; Account</em>.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>
                          <strong>Account Setup:</strong> Enter an <em>Account Name</em> (e.g., your business name). Review{' '}
                          <em>Data Sharing Settings</em> and click <em>Next</em>.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>
                          <strong>Property Setup:</strong> Enter a <em>Property Name</em> (e.g., your website URL), select your{' '}
                          <em>Reporting Time Zone</em> and <em>Currency</em>, then click <em>Next</em>.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>
                          <strong>Business Information:</strong> Choose an <em>Industry Category</em> and{' '}
                          <em>Business Size</em>. Select goals (or choose <em>Get baseline reports</em>) and click{' '}
                          <em>Create</em>.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>
                          <strong>Accept Terms:</strong> Read and accept the Google Analytics Terms of Service and{' '}
                          Data Processing Amendment.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>
                          <strong>Data Stream Setup:</strong> Choose <em>Web</em> for a website. Enter your site’s full URL
                          (e.g., <code>https://www.example.com</code>) and a <em>Stream Name</em> (e.g., “My Website
                          Stream”). Leave <em>Enhanced Measurement</em> enabled. Click <em>Create stream</em>.
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>
                          <strong>Install Your Google Tag:</strong> After creating the stream, you’ll see your{' '}
                          <em>Measurement ID</em> (starts with <code>G-</code>).
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>Return to Stan.Store and paste the Measurement ID above.</Text>
                      </List.Item>
                    </List>
                  </TextContainer>
                </Collapsible>
              </List.Item>
              <List.Item>
                Click <strong>Update</strong> to save your changes.
              </List.Item>
            </List>

            <Text variant="headingMd" as="h3" marginTop="4">
              Validate Installation
            </Text>
            <List type="bullet">
              <List.Item>
                After creating the stream, you’ll see your Measurement ID (starts with G-). Click <em>View tag instructions</em>, then choose <em>Install manually</em> and click <em>Test</em> to confirm the script is working.
              </List.Item>
            </List>

            <Divider />

            <Text variant="headingMd" as="h3" marginTop="4">
              Next Step: Integrate with Baobrain
            </Text>
            <Button primary url="/integrateGa4Only">
              Go to Integrations
            </Button>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
